<script setup lang="ts">
import { computed } from 'vue';

/**
 * Button — applies legacy .btn / .btn--primary / .btn--ghost classes.
 * Always use this instead of raw <button> so the design-kit stays consistent.
 */
type ButtonVariant = 'default' | 'primary' | 'ghost' | 'icon';
type ButtonType = 'button' | 'submit' | 'reset';

const props = withDefaults(
  defineProps<{
    variant?: ButtonVariant;
    type?: ButtonType;
    disabled?: boolean;
    loading?: boolean;
  }>(),
  {
    variant: 'default',
    type: 'button',
    disabled: false,
    loading: false,
  },
);

defineEmits<{
  click: [event: MouseEvent];
}>();

const classes = computed(() => ({
  btn: true,
  'btn--primary': props.variant === 'primary',
  'btn--ghost': props.variant === 'ghost',
  'btn--icon': props.variant === 'icon',
}));

const isDisabled = computed(() => props.disabled || props.loading);
</script>

<template>
  <button
    :type="type"
    :class="classes"
    :disabled="isDisabled"
    :aria-busy="loading || undefined"
    @click="$emit('click', $event)"
  >
    <slot></slot>
  </button>
</template>
